import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import { decryptData } from '../utils/cryptoUtils'; // Add this import
import './FileManagerPage.css';

const FileManagerPage = () => {
  const { currentUser, encryptionKey } = useContext(AuthContext); // Add encryptionKey
  const navigate = useNavigate();
  
  const [files, setFiles] = useState([]);
  const [orphanedFiles, setOrphanedFiles] = useState([]);
  const [harvestingFiles, setHarvestingFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orphanedLoading, setOrphanedLoading] = useState(false);
  const [harvestingLoading, setHarvestingLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [view, setView] = useState('all'); // 'all', 'orphaned', or 'harvesting'
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [orphanedFilesLoaded, setOrphanedFilesLoaded] = useState(false);
  const [harvestingFilesLoaded, setHarvestingFilesLoaded] = useState(false);

  // Ensure user has staff/admin access
  useEffect(() => {
    if (currentUser && !(
      currentUser.education ||
      currentUser.health ||
      currentUser.role === 'admin' ||
      currentUser.role === 'staff'
    )) {
      navigate('/staff-dashboard');
    }
  }, [currentUser, navigate]);

  // Load all files on component mount
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/files/admin/all');
        setFiles(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching files');
        setLoading(false);
      }
    };
    
    if (currentUser && (
      currentUser.education ||
      currentUser.health ||
      currentUser.role === 'admin' ||
      currentUser.role === 'staff'
    )) {
      fetchFiles();
    }
  }, [currentUser]);

  // Function to load orphaned files
  const loadOrphanedFiles = async () => {
    try {
      setOrphanedLoading(true);
      const response = await api.get('/api/files/admin/orphaned');
      setOrphanedFiles(response.data);
      setOrphanedFilesLoaded(true);
      setOrphanedLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching orphaned files');
      setOrphanedFilesLoaded(true);
      setOrphanedLoading(false);
    }
  };

  // Function to load harvesting files
  const loadHarvestingFiles = async () => {
    try {
      setHarvestingLoading(true);
      const response = await api.get('/api/files/admin/harvesting');
      setHarvestingFiles(response.data);
      setHarvestingFilesLoaded(true);
      setHarvestingLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching harvesting files');
      setHarvestingFilesLoaded(true);
      setHarvestingLoading(false);
    }
  };

  // When switching to orphaned view, load orphaned files only if we haven't loaded them yet
  useEffect(() => {
    if (view === 'orphaned' && !orphanedFilesLoaded && !orphanedLoading) {
      loadOrphanedFiles();
    }
    if (view === 'harvesting' && !harvestingFilesLoaded && !harvestingLoading) {
      loadHarvestingFiles();
    }
  }, [view, orphanedFilesLoaded, orphanedLoading, harvestingFilesLoaded, harvestingLoading]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format file size for display
  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Delete a file
  const deleteFile = async (id, filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }
    
    try {
      if (view === 'harvesting') {
        // Delete harvesting file by filename
        await api.delete(`/api/files/admin/harvesting/${filename}`);
        setHarvestingFiles(harvestingFiles.filter(file => file.filename !== filename));
      } else {
        // Delete grant application file by ID
        await api.delete(`/api/files/${id}`);
        
        // Update the files list
        if (view === 'all') {
          setFiles(files.filter(file => file._id !== id));
        } else {
          setOrphanedFiles(orphanedFiles.filter(file => file._id !== id));
        }
      }
      
      setMessage(`File ${filename} deleted successfully`);
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting file');
    }
  };

  // Run cleanup to delete all orphaned files
  const runCleanup = async () => {
    if (!window.confirm('Are you sure you want to delete all orphaned files? This cannot be undone.')) {
      return;
    }
    
    try {
      setCleanupInProgress(true);
      const response = await api.post('/api/files/admin/cleanup');
      
      setMessage(
        `Cleaned up ${response.data.deletedCount} of ${response.data.totalOrphaned} orphaned files. ` +
        (response.data.errors.length > 0 ? `${response.data.errors.length} errors occurred.` : '')
      );
      
      // Set orphaned files to empty array since they've been cleaned up
      setOrphanedFiles([]);
      
      // Also refresh the all files list
      const allFilesResponse = await api.get('/api/files/admin/all');
      setFiles(allFilesResponse.data);
      
      setCleanupInProgress(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error running cleanup');
      setCleanupInProgress(false);
    }
  };

  // Refresh the current view
  const refreshFiles = async () => {
    if (view === 'all') {
      setLoading(true);
      try {
        const response = await api.get('/api/files/admin/all');
        setFiles(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Error refreshing files');
      } finally {
        setLoading(false);
      }
    } else if (view === 'orphaned') {
      await loadOrphanedFiles();
    } else if (view === 'harvesting') {
      await loadHarvestingFiles();
    }
  };

  // Download a file
  const downloadFile = (id, filename) => {
    const token = localStorage.getItem('authToken');
    if (view === 'harvesting') {
      // Download harvesting file directly from uploads/temp
      window.open(`${api.defaults.baseURL}/uploads/temp/${filename}`, '_blank');
    } else {
      // Download grant file via API
      window.open(`${api.defaults.baseURL}/api/files/${id}?token=${token}`, '_blank');
    }
  };

  return (
    <div className="file-manager-container">
      <div className="file-manager-header">
        <h1>File Manager</h1>
        <div className="file-manager-nav">
          <a href="/staff-health-programs" className="fm-back-link">← Back to Programs</a>
        </div>
      </div>
      
      <div className="view-tabs">
        <button 
          className={view === 'all' ? 'active' : ''}
          onClick={() => setView('all')}
        >
          Grant Files ({files.length})
        </button>
        <button 
          className={view === 'harvesting' ? 'active' : ''}
          onClick={() => setView('harvesting')}
        >
          Harvesting Files {harvestingFiles.length > 0 && `(${harvestingFiles.length})`}
        </button>
        <button 
          className={view === 'orphaned' ? 'active' : ''}
          onClick={() => setView('orphaned')}
        >
          Orphaned Files {orphanedFiles.length > 0 && `(${orphanedFiles.length})`}
        </button>
      </div>
      
      {/* Error and message display */}
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      
      {/* View controls */}
      <div className="view-controls">
        {view === 'orphaned' && orphanedFilesLoaded && orphanedFiles.length > 0 && (
          <button 
            className="cleanup-button"
            onClick={runCleanup}
            disabled={orphanedLoading || cleanupInProgress}
          >
            {cleanupInProgress ? 'Cleaning...' : 'Cleanup All Orphaned Files'}
          </button>
        )}
        
        <button 
          className="refresh-button"
          onClick={refreshFiles}
          disabled={loading || orphanedLoading || cleanupInProgress}
        >
          ↻ Refresh
        </button>
      </div>
      
      {/* Files listing */}
      {(view === 'all' && loading) || (view === 'orphaned' && orphanedLoading) || (view === 'harvesting' && harvestingLoading) ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading files...</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="files-table">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Original Name</th>
                <th>Size</th>
                {view !== 'harvesting' && <th>User</th>}
                {view !== 'harvesting' && <th>Application</th>}
                {view === 'harvesting' && <th>Used In</th>}
                <th>Upload Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(view === 'all' ? files : view === 'orphaned' ? orphanedFiles : harvestingFiles).map(file => (
                <tr key={file._id || file.filename}>
                  <td className="filename-cell">
                    {file.filename}
                    {view !== 'harvesting' && file.existsOnDisk === false && (
                      <div className="file-flag file-flag-missing">Missing on disk</div>
                    )}
                    {view === 'harvesting' && file.isUnreferenced && (
                      <div className="file-flag file-flag-unreferenced">Unreferenced file</div>
                    )}
                  </td>
                  <td>{file.originalname || file.filename}</td>
                  <td>{formatSize(file.size)}</td>
                  {view !== 'harvesting' && (
                    <td>
                      {file.user 
                        ? (file.user.firstName && file.user.lastName)
                          ? `${decryptData(file.user.firstName, encryptionKey)} ${decryptData(file.user.lastName, encryptionKey)}`
                          : file.user.email 
                            ? decryptData(file.user.email, encryptionKey)
                            : file.user
                        : 'N/A'
                      }
                    </td>
                  )}
                  {view !== 'harvesting' && (
                    <td>
                      {file.applicationId 
                        ? `${file.applicationName || 'N/A'} (${file.applicantName || 'Unknown'})`
                        : 'Not attached'
                      }
                    </td>
                  )}
                  {view === 'harvesting' && (
                    <td>
                      {file.usedInHarvesting && file.usedIn && file.usedIn.length > 0
                        ? file.usedIn.map(use => use.title).join(', ')
                        : file.trackedInDatabase
                          ? 'Tracked by application record'
                          : 'Not used'}
                    </td>
                  )}
                  <td>{formatDate(file.uploadDate || file.createdAt || file.stats?.mtime)}</td>
                  <td>
                    <div className="file-actions">
                      <button 
                        onClick={() => downloadFile(file._id, view === 'harvesting' ? file.filename : file.originalname)}
                        className="download-btn"
                        title="Download"
                      >
                        ⬇️
                      </button>
                      <button 
                        onClick={() => deleteFile(file._id, view === 'harvesting' ? file.filename : file.originalname)}
                        className="delete-btn"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {((view === 'all' && files.length === 0) || 
                 (view === 'orphaned' && orphanedFilesLoaded && orphanedFiles.length === 0) ||
                 (view === 'harvesting' && harvestingFilesLoaded && harvestingFiles.length === 0)) ? (
                <tr>
                  <td colSpan="7" className="no-files-message">
                    {view === 'all' 
                      ? 'No grant application files found in the system.' 
                      : view === 'orphaned'
                      ? 'No orphaned files found. All files are properly linked to applications.'
                      : 'No harvesting files found in uploads/temp folder.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      
      {view === 'orphaned' && orphanedFilesLoaded && orphanedFiles.length > 0 && (
        <div className="cleanup-footer">
          <button 
            className="cleanup-button large"
            onClick={runCleanup}
            disabled={cleanupInProgress}
          >
            {cleanupInProgress ? 'Cleaning...' : `Clean up ${orphanedFiles.length} orphaned files`}
          </button>
        </div>
      )}
    </div>
  );
};

export default FileManagerPage;
